import { getCardinalSet } from "./vastu";

// Compute centroid of a polygon (array of {x, y})
// Uses the standard polygon centroid formula (area-weighted)
export function polygonCentroid(points) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) return { ...points[0] };
  if (points.length === 2) return { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };

  let area = 0;
  let cx = 0;
  let cy = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % n];
    const cross = p0.x * p1.y - p1.x * p0.y;
    area += cross;
    cx += (p0.x + p1.x) * cross;
    cy += (p0.y + p1.y) * cross;
  }
  area /= 2;
  if (Math.abs(area) < 1e-6) {
    // Degenerate polygon - fallback to average
    const avg = points.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
    return { x: avg.x / n, y: avg.y / n };
  }
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

// Compute polygon area (absolute)
export function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % n];
    area += p0.x * p1.y - p1.x * p0.y;
  }
  return Math.abs(area / 2);
}

// Convert canvas angle to compass angle
// Canvas: 0° = +X (right), positive Y goes DOWN
// Compass: 0° = North (up), clockwise
// So compass_deg = (canvas_angle_from_positive_y_ccw)... simpler:
// Given a delta (dx, dy) from center, compass angle = atan2(dx, -dy) in degrees, normalized to [0, 360)
export function vectorToCompassDeg(dx, dy) {
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}

// Apply rotation offset (true north correction)
// rotationDeg: how many degrees the drawing must be rotated CLOCKWISE to have true north at the top
export function applyNorthRotation(compassDeg, rotationDeg) {
  let d = (compassDeg - rotationDeg) % 360;
  if (d < 0) d += 360;
  return d;
}

// Compass angle where sector 0 begins: 8/16 are centred on North; the 32-pada grid has an edge at 0°.
export function sectorStartOffset(count) {
  return count === 32 ? 0 : -(360 / count) / 2;
}

// Given number of sectors, return sector index for angle
export function sectorIndexForAngle(compassDeg, count) {
  const size = 360 / count;
  let a = (compassDeg - sectorStartOffset(count)) % 360;
  if (a < 0) a += 360;
  return Math.floor(a / size) % count;
}

// Get sector angular range (start, end) for index [in compass deg, may wrap]
export function sectorRange(index, count) {
  const size = 360 / count;
  const start = (index * size + sectorStartOffset(count) + 360) % 360;
  const end = (start + size) % 360;
  const center = (start + size / 2) % 360;
  return { center, start, end, size };
}

// Compute intersection of a ray from center at compass angle deg with the bounding polygon (or bbox)
// Returns the point where the ray exits the polygon (or bbox if no polygon)
export function raycastToBox(center, compassDeg, bbox) {
  // Convert compass to canvas direction vector
  const rad = (compassDeg * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  // bbox = {minX, minY, maxX, maxY}
  const eps = 1e-9;
  let tMin = Infinity;
  if (Math.abs(dx) > eps) {
    const t1 = (bbox.minX - center.x) / dx;
    const t2 = (bbox.maxX - center.x) / dx;
    if (t1 > 0) tMin = Math.min(tMin, t1);
    if (t2 > 0) tMin = Math.min(tMin, t2);
  }
  if (Math.abs(dy) > eps) {
    const t1 = (bbox.minY - center.y) / dy;
    const t2 = (bbox.maxY - center.y) / dy;
    if (t1 > 0) tMin = Math.min(tMin, t1);
    if (t2 > 0) tMin = Math.min(tMin, t2);
  }
  if (!isFinite(tMin)) tMin = 100;
  return { x: center.x + dx * tMin, y: center.y + dy * tMin };
}

// Ray-polygon intersection - returns closest positive t intersection point
export function raycastToPolygon(center, compassDeg, polygon) {
  if (!polygon || polygon.length < 3) return null;
  const rad = (compassDeg * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  let closest = null;
  let bestT = Infinity;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    const seg = { x: b.x - a.x, y: b.y - a.y };
    const denom = dx * (-seg.y) - dy * (-seg.x);
    if (Math.abs(denom) < 1e-9) continue;
    const t = ((a.x - center.x) * (-seg.y) - (a.y - center.y) * (-seg.x)) / denom;
    const u = ((a.x - center.x) * dy - (a.y - center.y) * dx) / -denom;
    if (t >= 0 && u >= 0 && u <= 1) {
      if (t < bestT) {
        bestT = t;
        closest = { x: center.x + dx * t, y: center.y + dy * t };
      }
    }
  }
  return closest;
}

// Radius of the divisions circle: encloses the whole plan and extends just beyond
// the walls so the compass ring sits outside the outline.
export function sectorCircleRadius(center, polygon, bbox) {
  const pts = (polygon && polygon.length >= 3)
    ? polygon
    : [
        { x: bbox.minX, y: bbox.minY }, { x: bbox.maxX, y: bbox.minY },
        { x: bbox.maxX, y: bbox.maxY }, { x: bbox.minX, y: bbox.maxY },
      ];
  let maxD = 0;
  for (const p of pts) {
    const d = Math.hypot(p.x - center.x, p.y - center.y);
    if (d > maxD) maxD = d;
  }
  return maxD * 1.12;
}

// Build sectors data: array of { index, code, centerDeg, startDeg, endDeg, radius, vertices }
// Divisions form a full circle (pie slices) around the Brahmasthan, extending beyond the walls.
export function buildSectors(count, center, polygon, bbox, rotationDeg = 0) {
  const codes = getCardinalSet(count);
  const sectors = [];
  const size = 360 / count;
  const radius = sectorCircleRadius(center, polygon, bbox);
  for (let i = 0; i < count; i++) {
    const { start: startDeg, end: endDeg, center: centerDeg } = sectorRange(i, count);
    const drawStart = startDeg + rotationDeg;
    const drawEnd = drawStart + size;
    const steps = 8;
    const vertices = [{ ...center }];
    for (let s = 0; s <= steps; s++) {
      const a = drawStart + (drawEnd - drawStart) * (s / steps);
      const rad = (a * Math.PI) / 180;
      vertices.push({ x: center.x + Math.sin(rad) * radius, y: center.y - Math.cos(rad) * radius });
    }
    sectors.push({
      index: i,
      code: codes[i],
      centerDeg,
      startDeg,
      endDeg,
      radius,
      angleRange: startDeg > endDeg ? `${startDeg.toFixed(2)}° - 360° & 0° - ${endDeg.toFixed(2)}°` : `${startDeg.toFixed(2)}° - ${endDeg.toFixed(2)}°`,
      vertices,
    });
  }
  return sectors;
}

// Label anchor just outside the house outline along the sector's centre line.
// Returns { boundary, anchor, ux, uy } where (ux, uy) is the outward unit vector.
export function sectorLabelAnchor(sector, center, offset) {
  const boundary = sector.vertices[Math.floor(sector.vertices.length / 2)];
  let ux = boundary.x - center.x, uy = boundary.y - center.y;
  const len = Math.hypot(ux, uy) || 1;
  ux /= len; uy /= len;
  return { boundary, anchor: { x: boundary.x + ux * offset, y: boundary.y + uy * offset }, ux, uy };
}

// Compute bounding box from polygon (or default given canvas dims)
export function computeBBox(polygon, fallbackW, fallbackH) {
  if (!polygon || polygon.length === 0) {
    return { minX: 0, minY: 0, maxX: fallbackW, maxY: fallbackH };
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}


// Human-friendly degree label for a division, e.g. 45 -> "45°", 22.5 -> "22.5°".
export function formatDeg(deg) {
  const r = Math.round(((deg % 360) + 360) % 360 * 100) / 100;
  const s = Number.isInteger(r) ? String(r) : r.toFixed(2).replace(/\.?0+$/, "");
  return `${s}°`;
}
